import type { IssueGroup, ParsingIssue } from "@/features/patient/records/api/dataPipelineApi";

/** The issue's section — from the backend; the same rule locally when it's absent
 *  (the upload-time zip scan only ever reports unsupported files). */
export function issueGroup(issue: ParsingIssue): IssueGroup {
  if (issue.group) return issue.group;
  if (issue.error_type === "needs_review") return "note";
  if (issue.error_type === "unsupported_format" || issue.error_type === "UnreadableZip") return "unreadable";
  return "not_imported";
}

export function groupIssues(issues: ParsingIssue[]): Record<IssueGroup, ParsingIssue[]> {
  const out: Record<IssueGroup, ParsingIssue[]> = { unreadable: [], not_imported: [], note: [] };
  for (const issue of issues) out[issueGroup(issue)].push(issue);
  return out;
}

export const ERROR_TYPE_LABELS: Record<string, string> = {
  MissingRequiredField: "Missing required information",
  MappingException: "Unrecognized data format",
  ValidationError: "Unrecognized data format",
  unsupported_format: "Unsupported file format",
};

// "Patient" isn't shown as a browsable resource anywhere else in the app (see
// parsedDataPresentation.ts's HIDDEN_RESOURCE_TYPES). An issue on it here is only
// about the data in the file (e.g. a missing field) — whether the upload is the
// patient's is Module 5's identity check, shown separately (UploadIdentityCard).
export const RESOURCE_TYPE_LABELS: Record<string, string> = {
  Patient: "Patient details",
};

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}

/** Groups issues by (resource_type, source_file) and numbers each entry within a
 *  group ("2 of 3") so multiple genuinely distinct failed records of the same type
 *  from the same file don't render as indistinguishable duplicate cards. Also drops
 *  true duplicates — same record_id reported twice in the same group. */
export function prepareIssues(issues: ParsingIssue[]): (ParsingIssue & { ordinal?: string })[] {
  const groups = new Map<string, ParsingIssue[]>();
  const seenIdsByGroup = new Map<string, Set<string>>();

  for (const issue of issues) {
    const groupKey = `${issue.resource_type}|${issue.source_file ?? ""}`;
    if (issue.record_id) {
      const seenIds = seenIdsByGroup.get(groupKey) ?? new Set<string>();
      if (seenIds.has(issue.record_id)) continue;
      seenIds.add(issue.record_id);
      seenIdsByGroup.set(groupKey, seenIds);
    }
    const group = groups.get(groupKey) ?? [];
    group.push(issue);
    groups.set(groupKey, group);
  }

  return [...groups.values()].flatMap((group) =>
    group.map((issue, index) => (
      group.length > 1 ? { ...issue, ordinal: `${index + 1} of ${group.length}` } : issue
    ))
  );
}
